import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    FaUserTie, FaTrash, FaGavel, FaEye, FaTimes, FaSearch, FaFilter,
    FaRedo, FaChevronLeft, FaChevronRight, FaCheckCircle, FaExclamationCircle, FaShieldAlt
} from "react-icons/fa";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

const TotalAdvocates = () => {
    const [advocates, setAdvocates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [actionLoading, setActionLoading] = useState(null);
    const [selectedAdvocate, setSelectedAdvocate] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [filterStatus, setFilterStatus] = useState("All");

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;

    const fetchAdvocates = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/admin/advocates/all`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );

            const data = response.data.data || [];
            if (Array.isArray(data)) {
                setAdvocates(data);
            } else {
                setAdvocates([]);
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Failed to fetch advocates");
            setAdvocates([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdvocates();
    }, []);

    const handleAction = async (id, action) => {
        try {
            setActionLoading(id);
            const endpoint = action === 'approve'
                ? `${process.env.REACT_APP_API_BASE_URL}/admin/advocate/approve/${id}`
                : `${process.env.REACT_APP_API_BASE_URL}/admin/advocate/reject/${id}`;

            const response = await axios.put(
                endpoint,
                {},
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );

            if (response.data.success) {
                toast.success(`Advocate ${action}d successfully`);
                setAdvocates(prev => prev.map(adv =>
                    adv._id === id
                        ? { ...adv, accountStatus: action === 'approve' ? 'Approved' : 'Rejected' }
                        : adv
                ));
            }
        } catch (err) {
            toast.error(err.response?.data?.message || `Failed to ${action} advocate`);
        } finally {
            setActionLoading(null);
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Confirm Termination?',
            text: "This will permanently revoke access and wipe associated wallet data. This procedure is irreversible.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#e11d48',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'YES, Logout',
            cancelButtonText: 'ABORT',
            background: '#ffffff',
            borderRadius: '2rem'
        });

        if (result.isConfirmed) {
            try {
                setActionLoading(id);
                const response = await axios.delete(
                    `${process.env.REACT_APP_API_BASE_URL}/admin/advocate/delete/${id}`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${localStorage.getItem("token")}`,
                        },
                    }
                );

                if (response.data.success) {
                    Swal.fire({
                        title: 'Revoked!',
                        text: 'Advocate credentials have been purged.',
                        icon: 'success',
                        background: '#ffffff',
                        borderRadius: '2rem'
                    });
                    setAdvocates(prev => prev.filter(adv => adv._id !== id));
                }
            } catch (err) {
                toast.error(err.response?.data?.message || "Purge failed");
            } finally {
                setActionLoading(null);
            }
        }
    };

    const handleViewDetails = (advocate) => {
        setSelectedAdvocate(advocate);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedAdvocate(null);
    };

    const stats = {
        total: advocates.length,
        approved: advocates.filter(a => a.accountStatus === "Approved").length,
        pending: advocates.filter(a => !a.accountStatus || a.accountStatus === "Pending").length,
        rejected: advocates.filter(a => a.accountStatus === "Rejected").length
    };

    const filteredAdvocates = advocates.filter((advocate) => {
        const term = searchTerm.toLowerCase();
        const matchesSearch =
            (advocate.name && advocate.name.toLowerCase().includes(term)) ||
            (advocate.email && advocate.email.toLowerCase().includes(term)) ||
            (advocate.phoneNo && advocate.phoneNo.toString().toLowerCase().includes(term)) ||
            (advocate.city && advocate.city.toLowerCase().includes(term)) ||
            (advocate.state && advocate.state.toLowerCase().includes(term)) ||
            (advocate.advocateId && advocate.advocateId.toLowerCase().includes(term)) ||
            (advocate.specialization && advocate.specialization.toLowerCase().includes(term));

        if (filterStatus === "All") return matchesSearch;
        if (filterStatus === "Pending") return matchesSearch && (!advocate.accountStatus || advocate.accountStatus === "Pending");
        return matchesSearch && advocate.accountStatus === filterStatus;
    }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredAdvocates.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredAdvocates.length / itemsPerPage);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterStatus]);

    const statusConfigs = {
        Approved: { bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-100", icon: <FaCheckCircle /> },
        Rejected: { bg: "bg-red-50", text: "text-red-600", border: "border-red-100", icon: <FaExclamationCircle /> },
        Pending: { bg: "bg-amber-50", text: "text-amber-600", border: "border-amber-100", icon: <FaShieldAlt /> }
    };

    return (
        <div className="min-h-screen bg-[#f8fafc] p-4 md:p-8 font-sans">
            <div className="fixed top-0 left-0 w-full h-1 bg-gradient-to-r from-slate-700 via-blue-800 to-indigo-900 z-50"></div>

            {/* Page Header - Compact */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#1e293b] rounded-xl flex items-center justify-center text-white text-lg shadow-lg shadow-slate-900/10">
                        <FaGavel />
                    </div>
                    <div>
                        <h1 className="text-xl font-black text-[#1e293b] tracking-tight">Legal Council</h1>
                        <p className="text-[#94a3b8] text-[10px] font-black uppercase tracking-widest">
                            {advocates.length} Certified Experts
                        </p>
                    </div>
                </div>
                <button
                    onClick={fetchAdvocates}
                    className="flex items-center gap-2 px-4 py-2 bg-white text-[#475569] border border-[#e2e8f0] rounded-xl shadow-sm hover:bg-gray-50 transition-all font-bold text-xs"
                >
                    <FaRedo className={`${loading ? 'animate-spin' : ''}`} />
                    Sync
                </button>
            </div>

            {/* Premium Stats Grid - Filled & Compact */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
                {["All", "Approved", "Pending", "Rejected"].map(status => (
                    <div
                        key={status}
                        onClick={() => setFilterStatus(status)}
                        className={`cursor-pointer p-3 rounded-2xl border transition-all relative overflow-hidden ${filterStatus === status
                            ? "bg-white border-blue-500 shadow-md border-2"
                            : "bg-gray-50 border-transparent hover:bg-gray-100"
                            }`}
                    >
                        <div className={`text-[9px] font-black uppercase tracking-widest mb-0.5 ${filterStatus === status ? 'text-blue-600' : 'text-[#94a3b8]'}`}>
                            {status === 'All' ? 'Total' : status}
                        </div>
                        <div className="text-lg font-black text-[#1e293b]">
                            {status === 'All' ? stats.total : stats[status.toLowerCase()]}
                        </div>
                        {filterStatus === status && (
                            <div className="absolute top-0 right-0 w-8 h-8 bg-blue-50 rounded-bl-3xl flex items-center justify-center text-blue-500">
                                <FaFilter size={10} />
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-slate-500/5 border border-[#f1f5f9] overflow-hidden">
                <div className="p-6 md:p-8 border-b border-[#f1f5f9] flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-100 text-slate-600 rounded-xl flex items-center justify-center text-lg shadow-sm">
                            <FaSearch />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-[#1e293b]">Advocate Database</h3>
                            <p className="text-xs text-[#94a3b8] font-bold uppercase tracking-tighter">Current Filter: {filterStatus}</p>
                        </div>
                    </div>
                    <div className="relative w-full md:w-96 group">
                        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94a3b8] group-focus-within:text-slate-800 transition-colors" />
                        <input
                            type="text"
                            placeholder="Find by name, specialization, ID..."
                            className="w-full pl-12 pr-4 py-3.5 bg-[#f8fafc] border-transparent rounded-2xl outline-none focus:ring-4 focus:ring-slate-500/10 focus:bg-white focus:border-slate-200 transition-all text-sm font-medium text-[#334155]"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="bg-[#f8fafc]">
                                <th className="px-6 py-4 text-left text-[10px] font-black text-[#64748b] uppercase tracking-widest border-b border-[#f1f5f9]">#</th>
                                <th className="px-6 py-4 text-left text-[10px] font-black text-[#64748b] uppercase tracking-widest border-b border-[#f1f5f9]">Legal Expert</th>
                                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Legal ID</th>
                                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest text-center">Focus Area</th>
                                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest">Geography</th>
                                <th className="px-8 py-5 text-left text-xs font-black text-[#64748b] uppercase tracking-widest text-center">Status</th>
                                <th className="px-8 py-5 text-center text-xs font-black text-[#64748b] uppercase tracking-widest">Operations</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#f1f5f9]">
                            {loading ? (
                                Array(5).fill(0).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan="6" className="px-8 py-8"><div className="h-10 bg-gray-100 rounded-2xl w-full"></div></td>
                                    </tr>
                                ))
                            ) : currentItems.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-8 py-20 text-center text-[#94a3b8] font-black uppercase tracking-widest">No legal experts found</td>
                                </tr>
                            ) : (
                                currentItems.map((advocate, idx) => (
                                    <tr key={advocate._id} className="hover:bg-gray-50 transition-all duration-150">
                                        <td className="px-6 py-3 whitespace-nowrap text-[10px] font-black text-gray-400">
                                            {(currentPage - 1) * itemsPerPage + idx + 1}
                                        </td>
                                        <td className="px-6 py-3 whitespace-nowrap">
                                            <div className="flex items-center gap-3">
                                                <div className="relative">
                                                    {advocate.profileImage ? (
                                                        <img src={advocate.profileImage} alt={advocate.name} className="w-10 h-10 rounded-xl object-cover border-2 border-white shadow-md shadow-slate-500/10" />
                                                    ) : (
                                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-600 to-slate-900 flex items-center justify-center text-white text-sm">
                                                            <FaUserTie />
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-black text-[#1e293b] uppercase tracking-tight truncate max-w-[120px]">{advocate.name || "Anonymous expert"}</p>
                                                    <p className="text-[9px] text-[#94a3b8] font-extrabold uppercase tracking-wider truncate max-w-[120px]">{advocate.email}</p>
                                                </div>
                                            </div>
                                        </td>

                                        <td className="px-6 py-3 whitespace-nowrap text-xs">
                                            <div className="flex flex-col gap-1">
                                                <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded-md text-[9px] font-black uppercase tracking-widest border border-blue-100 shadow-sm w-fit">
                                                    {advocate.advocateId || 'REF'}
                                                </span>
                                                <span className="text-[9px] text-gray-400 font-bold uppercase">{advocate.experience || '0'}yr exp</span>
                                            </div>
                                        </td>

                                        <td className="px-6 py-3 whitespace-nowrap text-center">
                                            <span className="px-2 py-0.5 bg-purple-50 text-purple-600 rounded-md text-[9px] font-black uppercase tracking-widest border border-purple-100">
                                                {advocate.specialization || "GENERAL"}
                                            </span>
                                        </td>

                                        <td className="px-6 py-3 whitespace-nowrap text-xs">
                                            <div className="flex flex-col">
                                                <span className="font-black text-[#1e293b] uppercase tracking-tighter">{advocate.city || "Rest of India"}</span>
                                                <span className="text-[9px] font-bold text-[#94a3b8] uppercase">{advocate.state || "National"}</span>
                                            </div>
                                        </td>

                                        <td className="px-6 py-3 whitespace-nowrap text-center">
                                            <div className="flex items-center justify-center">
                                                {(() => {
                                                    const config = statusConfigs[advocate.accountStatus || 'Pending'];
                                                    return (
                                                        <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5 ${config.text} border ${config.border} bg-white shadow-sm`}>
                                                            {advocate.accountStatus || 'Pending'}
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                        </td>

                                        <td className="px-6 py-3 whitespace-nowrap text-center">
                                            <div className="flex items-center justify-center gap-1.5">
                                                <button
                                                    onClick={() => handleViewDetails(advocate)}
                                                    className="w-7 h-7 flex items-center justify-center bg-gray-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded-lg transition-all border border-gray-100"
                                                    title="Inspect Profile"
                                                >
                                                    <FaEye size={12} />
                                                </button>

                                                {advocate.accountStatus === 'Approved' ? (
                                                    <button
                                                        onClick={() => handleAction(advocate._id, 'reject')}
                                                        disabled={actionLoading === advocate._id}
                                                        className="px-2 py-1 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white rounded-lg text-[8px] font-black uppercase transition-all shadow-sm"
                                                    >
                                                        Revoke
                                                    </button>
                                                ) : advocate.accountStatus === 'Rejected' ? (
                                                    <button
                                                        onClick={() => handleAction(advocate._id, 'approve')}
                                                        disabled={actionLoading === advocate._id}
                                                        className="px-2 py-1 bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-lg text-[8px] font-black uppercase transition-all shadow-sm"
                                                    >
                                                        Relaunch
                                                    </button>
                                                ) : (
                                                    <>
                                                        <button
                                                            onClick={() => handleAction(advocate._id, 'approve')}
                                                            disabled={actionLoading === advocate._id}
                                                            className="w-7 h-7 flex items-center justify-center bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-lg transition-all border border-emerald-100"
                                                        >
                                                            <FaCheckCircle size={12} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleAction(advocate._id, 'reject')}
                                                            disabled={actionLoading === advocate._id}
                                                            className="w-7 h-7 flex items-center justify-center bg-red-50 text-red-600 hover:bg-red-600 hover:text-white rounded-lg transition-all border border-red-100"
                                                        >
                                                            <FaTimes size={12} />
                                                        </button>
                                                    </>
                                                )}

                                                <button
                                                    onClick={() => handleDelete(advocate._id)}
                                                    disabled={actionLoading === advocate._id}
                                                    className="w-7 h-7 flex items-center justify-center bg-red-50 text-red-500 hover:bg-red-600 hover:text-white rounded-lg transition-all border border-red-100"
                                                >
                                                    <FaTrash size={12} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="p-8 bg-[#f8fafc] border-t border-[#f1f5f9] flex flex-col md:flex-row items-center justify-between gap-4 text-xs font-bold uppercase tracking-widest text-[#94a3b8]">
                        <p>Segment {currentPage} of {totalPages}</p>
                        <div className="flex items-center gap-2 bg-white p-1 rounded-2xl border border-gray-100 shadow-sm">
                            <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="w-10 h-10 flex items-center justify-center hover:bg-gray-50 disabled:opacity-30"><FaChevronLeft size={10} /></button>
                            <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="w-10 h-10 flex items-center justify-center hover:bg-gray-50 disabled:opacity-30"><FaChevronRight size={10} /></button>
                        </div>
                    </div>
                )}
            </div>

            {/* Premium Details Modal */}
            {showModal && selectedAdvocate && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#0f172a]/80 backdrop-blur-xl animate-in fade-in duration-300">
                    <div className="bg-white rounded-[3rem] shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden border border-white/20 animate-in zoom-in-95 duration-300 flex flex-col">

                        <div className="relative h-60 bg-gradient-to-br from-slate-800 via-slate-900 to-indigo-950 p-12 flex items-end">
                            <button onClick={closeModal} className="absolute top-8 right-8 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white backdrop-blur-md transition-all active:scale-95 shadow-xl">
                                <FaTimes />
                            </button>
                            <div className="flex items-center gap-8">
                                <div className="w-32 h-32 rounded-[2.5rem] bg-white p-2 shadow-2xl ring-8 ring-white/10">
                                    {selectedAdvocate.profileImage ? (
                                        <img src={selectedAdvocate.profileImage} alt={selectedAdvocate.name} className="w-full h-full object-cover rounded-[2.2rem]" />
                                    ) : (
                                        <div className="w-full h-full bg-slate-100 rounded-[2.2rem] flex items-center justify-center text-slate-400 text-4xl"><FaUserTie /></div>
                                    )}
                                </div>
                                <div>
                                    <h2 className="text-4xl font-black text-white uppercase tracking-tight">{selectedAdvocate.name}</h2>
                                    <div className="flex items-center gap-3 mt-2">
                                        <span className={`px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${statusConfigs[selectedAdvocate.accountStatus || 'Pending'].bg} ${statusConfigs[selectedAdvocate.accountStatus || 'Pending'].text}`}>
                                            {selectedAdvocate.accountStatus || 'Verification Required'}
                                        </span>
                                        <span className="text-white/40 text-[10px] font-bold uppercase tracking-widest">Reg ID: {selectedAdvocate.advocateId}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-12 overflow-y-auto custom-scrollbar grid grid-cols-1 md:grid-cols-3 gap-12 bg-white">

                            <div className="space-y-8">
                                <section>
                                    <h4 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-500"></div> Core Identity
                                    </h4>
                                    <div className="space-y-4">
                                        <div><p className="text-[9px] font-bold text-gray-400 uppercase">Bar Association</p><p className="font-bold text-[#1e293b]">{selectedAdvocate.barAssociationCourt}</p></div>
                                        <div><p className="text-[9px] font-bold text-gray-400 uppercase">Contact Terminal</p><p className="font-bold text-[#1e293b]">{selectedAdvocate.phoneNo}</p><p className="text-xs text-gray-500 font-medium">{selectedAdvocate.email}</p></div>
                                        <div><p className="text-[9px] font-bold text-gray-400 uppercase">Legal Specialization</p><p className="font-bold text-indigo-600">{selectedAdvocate.specialization}</p></div>
                                    </div>
                                </section>
                            </div>

                            <div className="space-y-8">
                                <section>
                                    <h4 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-500"></div> Legislative Data
                                    </h4>
                                    <div className="space-y-4 p-6 bg-slate-50 rounded-3xl border border-slate-100">
                                        <div><p className="text-[9px] font-bold text-gray-400 uppercase">Aadhar Index</p><p className="font-bold font-mono text-slate-700">{selectedAdvocate.aadharNo}</p></div>
                                        <div><p className="text-[9px] font-bold text-gray-400 uppercase">PAN Tax ID</p><p className="font-bold font-mono text-slate-700">{selectedAdvocate.pancard}</p></div>
                                        <div><p className="text-[9px] font-bold text-gray-400 uppercase">Council Experience</p><p className="font-bold text-slate-700">{selectedAdvocate.experience} Technical Years</p></div>
                                    </div>
                                </section>
                            </div>

                            <div className="space-y-8">
                                <section>
                                    <h4 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-500"></div> Geographic Index
                                    </h4>
                                    <div className="space-y-4">
                                        <div><p className="text-[9px] font-bold text-gray-400 uppercase">Residential Axis</p><p className="font-bold text-[#1e293b] text-sm leading-relaxed">{selectedAdvocate.residenceAddress}</p></div>
                                        <div><p className="text-[9px] font-bold text-gray-400 uppercase">Jurisdiction</p><p className="font-bold text-[#1e293b]">{selectedAdvocate.city}, {selectedAdvocate.state} - {selectedAdvocate.pincode}</p></div>
                                        <div><p className="text-[9px] font-bold text-gray-400 uppercase">Digital Onboarding</p><p className="font-bold text-[#1e293b]">{new Date(selectedAdvocate.createdAt).toLocaleString()}</p></div>
                                    </div>
                                </section>
                            </div>

                        </div>

                        <div className="p-8 bg-slate-50 flex justify-end items-center gap-4 border-t border-slate-100 shadow-inner">
                            <span className="text-[10px] font-black text-[#94a3b8] uppercase tracking-widest mr-auto pl-4">Council Audit Logging Active</span>
                            <button onClick={closeModal} className="px-10 py-4 bg-[#1e293b] hover:bg-black text-white font-black rounded-2xl text-xs transition-all uppercase tracking-widest shadow-xl shadow-slate-900/10 active:scale-95">Dismiss Index</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TotalAdvocates;
